/* ----------------------------------------------------------------------------
 * handsfree — the wire client for the USER-scoped Aura mic-mode preference
 * (GET/PUT /v1/ai/aura/handsfree). This is a PERSONAL account setting stored on
 * bc_users (it follows the human across every workspace), so the endpoint is
 * self-service (a read-only workspace member may set their OWN mode) — distinct
 * from the workspace, write-role-gated voice-settings route. Cookie-session +
 * CSRF ride through the shared client; NO provider key touches the browser.
 *
 * SAFE DEFAULT: any read failure / unset value resolves to 'standard' so the
 * wake engine can NEVER arm before an explicit opt-in (matches the server-side
 * safe default when the column is absent pre-migration).
 * -------------------------------------------------------------------------- */
import { api, apiRequest, ApiError, USE_MOCK_DATA } from '@/lib/api';
import { coerceMicMode, type AuraMicMode } from '@/voice/wake/handsFreeConfig';

/** The user's hands-free preference + durable, versioned consent record. */
export interface HandsFreePrefs {
  micMode: AuraMicMode;
  /** UTC of the explicit always-on-mic consent (null = never consented). */
  consentAt: string | null;
  /** consent-copy version acknowledged (null = none). */
  consentVer: string | null;
}

/** snake_case wire DTO (mirrors the API envelope). */
interface HandsFreeDto {
  ok?: boolean;
  mic_mode?: string;
  consent_at?: string | null;
  consent_ver?: string | null;
}

/** Raised when the server requires explicit consent to enable a wake mode
 *  (HTTP 409 consent_required) — the UI must show the consent panel first. */
export class HandsFreeConsentRequiredError extends Error {
  constructor() {
    super('consent_required');
    this.name = 'HandsFreeConsentRequiredError';
  }
}

/** Adapt the wire DTO to the client shape, SAFE-defaulting the mode to standard. */
export function adaptHandsFree(dto: HandsFreeDto | undefined): HandsFreePrefs {
  return {
    micMode: coerceMicMode(dto?.mic_mode),
    consentAt: dto?.consent_at ?? null,
    consentVer: dto?.consent_ver ?? null,
  };
}

/** GET the caller's own mic mode + consent (defaults to standard when unset). */
export async function getHandsFreePrefs(signal?: AbortSignal): Promise<HandsFreePrefs> {
  if (USE_MOCK_DATA) return { micMode: 'standard', consentAt: null, consentVer: null };
  const res = await api.get<HandsFreeDto>('/ai/aura/handsfree', undefined, signal);
  return adaptHandsFree(res);
}

/**
 * PUT (persist) the caller's OWN mic mode. Enabling a wake mode
 * (continuous/both) requires `consentAck` unless a valid prior consent is on
 * file — the server stamps a versioned, durable consent record. Throws
 * HandsFreeConsentRequiredError on a 409 so the caller can surface the consent
 * panel and retry with consentAck=true.
 */
export async function saveMicMode(micMode: AuraMicMode, consentAck = false): Promise<HandsFreePrefs> {
  if (USE_MOCK_DATA) return { micMode, consentAt: consentAck ? new Date().toISOString() : null, consentVer: consentAck ? 'v1' : null };
  try {
    const res = await apiRequest<HandsFreeDto>('/ai/aura/handsfree', {
      method: 'PUT',
      body: { mic_mode: micMode, consent_ack: consentAck },
    });
    return adaptHandsFree(res);
  } catch (err) {
    if (err instanceof ApiError && (err.status === 409 || err.code === 'consent_required')) {
      throw new HandsFreeConsentRequiredError();
    }
    throw err;
  }
}
